/**
 * Immagine dell'anteprima della mail, per far vedere al modello il risultato del suo lavoro.
 *
 * Niente libreria esterna: l'HTML delle mail e' gia' tutto a stili inline, che e' esattamente
 * la condizione che rende praticabile la strada del browser — si impacchetta l'HTML dentro un
 * <foreignObject> di un SVG, si carica l'SVG come immagine e la si disegna su un canvas.
 * Un rasterizzatore generico (html2canvas e simili) serve quando gli stili stanno in fogli
 * esterni e vanno risolti: qui non e' il caso.
 *
 * Due vincoli di quella strada, entrambi gestiti qui:
 * - dentro un SVG caricato come immagine NON si scarica nulla dalla rete: ogni <img> e ogni
 *   sfondo va convertito prima in data: URI, altrimenti sparisce e il modello crede che manchi;
 * - il contenuto del foreignObject deve essere XML valido, quindi si passa dal DOM e da
 *   XMLSerializer invece che da manipolazione di stringhe (entita' e tag si sistemano da soli).
 *
 * Ogni errore porta a null: meglio nessuna immagine che un rettangolo bianco, che il modello
 * leggerebbe come "ho prodotto una mail vuota".
 */

// Lato lungo massimo accettato dall'API senza riduzione a valle
var MAX_SIDE = 1568
var EMAIL_WIDTH = 600

/**
 * Input:  url dell'immagine
 * Fa:     la scarica e la converte in data: URI. Funziona per le immagini del tenant (stessa
 *         origine); un dominio esterno senza CORS fallisce, ed e' previsto
 * Output: Promise<string|null>
 */
function toDataUri(url) {
	return fetch(url, { credentials: 'same-origin' })
		.then(function(res) { return res.ok ? res.blob() : null })
		.then(function(blob) {
			if (!blob) return null
			return new Promise(function(resolve) {
				var reader = new FileReader()
				reader.onload = function() { resolve(String(reader.result)) }
				reader.onerror = function() { resolve(null) }
				reader.readAsDataURL(blob)
			})
		})
		.catch(function() { return null })
}

/** Riquadro grigio al posto di un'immagine irraggiungibile: tiene l'ingombro, non finge una foto */
function placeholderDataUri() {
	return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(
		'<svg xmlns="http://www.w3.org/2000/svg" width="600" height="200">' +
		'<rect width="600" height="200" fill="#e0e0e0"/></svg>')
}

/**
 * Fa: sostituisce in blocco tutte le sorgenti remote (tag img e sfondi inline) con data: URI
 */
function inlineImages(host) {
	var jobs = []

	var imgs = host.querySelectorAll('img')
	for (var i = 0; i < imgs.length; i++) {
		(function(img) {
			var src = img.getAttribute('src') || ''
			if (!/^https?:\/\//i.test(src)) return
			jobs.push(toDataUri(src).then(function(uri) { img.setAttribute('src', uri || placeholderDataUri()) }))
		})(imgs[i])
	}

	var all = host.querySelectorAll('[style*="background-image"]')
	for (var k = 0; k < all.length; k++) {
		(function(el) {
			var match = /url\(['"]?(https?:\/\/[^'")]+)['"]?\)/i.exec(el.getAttribute('style') || '')
			if (!match) return
			jobs.push(toDataUri(match[1]).then(function(uri) {
				if (!uri) return
				el.setAttribute('style', el.getAttribute('style').replace(match[1], uri))
			}))
		})(all[k])
	}

	return Promise.all(jobs)
}

/** Almeno l'1% di pixel non bianchi: sotto, il disegno non e' avvenuto */
function hasContent(ctx, canvas) {
	try {
		var d = ctx.getImageData(0, 0, canvas.width, canvas.height).data
		var painted = 0
		for (var i = 0; i < d.length; i += 4) {
			if (d[i] < 245 || d[i + 1] < 245 || d[i + 2] < 245) painted++
		}
		return painted / (d.length / 4) > 0.01
	} catch (e) {
		return true // canvas non leggibile: si manda comunque, e' il caso meno probabile
	}
}

/**
 * Input:  html della mail (quello dell'anteprima, senza gli aiuti dell'editor)
 * Fa:     lo rasterizza in un JPEG ridotto, pronto da mandare al modello
 * Output: Promise<{media_type, data}|null>
 */
export function renderEmailToImage(html) {
	if (!html || typeof document === 'undefined') return Promise.resolve(null)

	var host = document.createElement('div')
	host.setAttribute('style', 'position:absolute;left:-99999px;top:0;width:' + EMAIL_WIDTH + 'px;background:#ffffff;')
	host.innerHTML = html
	document.body.appendChild(host)

	var cleanup = function() { if (host.parentNode) host.parentNode.removeChild(host) }

	return inlineImages(host).then(function() {
		var height = Math.max(1, host.scrollHeight)

		// Si serializza una COPIA senza il posizionamento fuori schermo: quello stile serve a
		// nascondere il contenitore nella pagina, ma dentro il foreignObject sposterebbe il
		// disegno a -99999px e l'immagine uscirebbe bianca (senza alcun errore)
		var clone = host.cloneNode(true)
		clone.setAttribute('style', 'width:' + EMAIL_WIDTH + 'px;background:#ffffff;')

		// XMLSerializer risolve entita' e chiude i tag: dentro l'SVG serve XML valido
		var xhtml = new XMLSerializer().serializeToString(clone)
		var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + EMAIL_WIDTH + '" height="' + height + '">' +
			'<foreignObject width="100%" height="100%">' + xhtml + '</foreignObject></svg>'

		return new Promise(function(resolve) {
			var img = new Image()
			var done = false
			var finish = function(value) { if (!done) { done = true; cleanup(); resolve(value) } }

			// Un SVG malformato non lancia: l'onload non arriva e basta
			setTimeout(function() { finish(null) }, 8000)

			img.onerror = function() { finish(null) }
			img.onload = function() {
				try {
					var scale = Math.min(1, MAX_SIDE / Math.max(EMAIL_WIDTH, height))
					var canvas = document.createElement('canvas')
					canvas.width = Math.max(1, Math.round(EMAIL_WIDTH * scale))
					canvas.height = Math.max(1, Math.round(height * scale))
					var ctx = canvas.getContext('2d')
					ctx.fillStyle = '#ffffff'
					ctx.fillRect(0, 0, canvas.width, canvas.height)
					ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

					// Guardia: un SVG che non disegna non lancia nessun errore, restituisce un
					// rettangolo bianco. Mandarlo sarebbe peggio di non mandare niente, perche'
					// il modello lo leggerebbe come "la mail e' venuta vuota"
					if (!hasContent(ctx, canvas)) return finish(null)

					var dataUrl = canvas.toDataURL('image/jpeg', 0.85)
					finish({ media_type: 'image/jpeg', data: dataUrl.split(',')[1] })
				} catch (e) {
					finish(null)
				}
			}
			img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
		})
	}).catch(function() {
		cleanup()
		return null
	})
}
